import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { X, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { thumbUrl } from '../utils/imageUrl';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const PropertyMapView = ({ properties, filters }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markers = useRef([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const navigate = useNavigate();

  // Filter properties based on search filters
  const filteredProperties = properties.filter(property => {
    if (!property.coordinates || !property.coordinates.lat || !property.coordinates.lng) {
      return false;
    }

    // Sanity-check coords before plotting. Anything outside Quebec province
    // is almost always a stale geocode or a test-fixture leak (e.g. dummy
    // properties that ended up at Ottawa coords). Plotting those at
    // arbitrary places would mislead guests, so we drop them silently.
    // Quebec bounding box: roughly lat 45–63, lng -80 to -56.
    const { lat, lng } = property.coordinates;
    if (lat < 44.5 || lat > 63 || lng < -80 || lng > -56) {
      return false;
    }

    // Apply price filter
    if (filters?.minPrice && property.price < filters.minPrice) return false;
    if (filters?.maxPrice && property.price > filters.maxPrice) return false;

    // Apply category filter
    if (filters?.category && property.category !== filters.category) return false;

    // Apply guest filter
    if (filters?.guests && property.guests < filters.guests) return false;

    return true;
  });

  useEffect(() => {
    if (map.current) return; // Initialize map only once

    if (filteredProperties.length === 0) {
      return;
    }

    // Initialize map
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [-73.5673, 45.5017], // Montreal default
      zoom: 12
    });

    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Wait for map to load before adding sources/layers
    map.current.on('load', () => {
      // Calculate bounds to fit all properties
      if (filteredProperties.length > 0) {
        const bounds = new mapboxgl.LngLatBounds();
        filteredProperties.forEach(property => {
          bounds.extend([property.coordinates.lng, property.coordinates.lat]);
        });

        map.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: 14
        });
      }
    });

  }, []);

  useEffect(() => {
    if (!map.current) return;

    // Wait for style to load
    if (!map.current.isStyleLoaded()) {
      const handler = () => {
        updateMarkers();
      };
      map.current.once('styledata', handler);
      return () => map.current.off('styledata', handler);
    }

    updateMarkers();

  }, [filteredProperties, selectedProperty]);

  const updateMarkers = () => {
    // Clear existing markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    // Add markers for each filtered property at their TRUE coordinates.
    //
    // Why no spatial offset?  We previously scattered overlapping markers
    // by adding a `0.001 × count` lat/lng offset (~110 m × count). For
    // multi-unit buildings (e.g. 32 Decarie units sharing one address),
    // that pushed Unit #32 over 3 km from its real location. Mapbox
    // already handles overlapping markers gracefully via z-index — the
    // top one is clickable, neighbours are accessible by zooming in. For
    // dense clusters, callers can wrap this in a clustering source.
    filteredProperties.forEach((property) => {
      const lng = property.coordinates.lng;
      const lat = property.coordinates.lat;

      // Create custom marker element with price
      const el = document.createElement('div');
      el.className = 'property-marker';
      el.innerHTML = `
        <div class="price-tag ${selectedProperty?._id === property._id ? 'selected' : ''}">
          $${Math.round(property.price)}
        </div>
      `;

      el.addEventListener('click', () => {
        setSelectedProperty(property);
        map.current.flyTo({
          center: [lng, lat],
          zoom: 15,
          duration: 1000
        });
      });

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([lng, lat])
        .addTo(map.current);

      markers.current.push(marker);
    });

    // Update bounds if filters changed
    if (filteredProperties.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      filteredProperties.forEach(property => {
        bounds.extend([property.coordinates.lng, property.coordinates.lat]);
      });

      map.current.fitBounds(bounds, {
        padding: 50,
        maxZoom: 14,
        duration: 1000
      });
    }
  };

  const handlePropertyCardClick = (property) => {
    navigate(`/property/${property._id}`);
  };

  const closePreview = () => {
    setSelectedProperty(null);
  };

  if (filteredProperties.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
        <div className="text-center p-8">
          <p className="text-gray-600 text-lg">No properties with locations to display</p>
          <p className="text-gray-500 text-sm mt-2">Try adjusting your filters</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      <div ref={mapContainer} className="w-full h-full rounded-lg" />
      
      <style>{`
        .property-marker {
          cursor: pointer;
          transition: transform 0.2s;
        }

        .property-marker:hover {
          transform: scale(1.15);
          z-index: 10 !important;
        }

        .price-tag {
          background: white;
          border: 2px solid #1f2937;
          border-radius: 24px;
          padding: 8px 16px;
          font-weight: 700;
          font-size: 14px;
          color: #1f2937;
          box-shadow: 0 2px 12px rgba(0,0,0,0.15);
          white-space: nowrap;
          transition: all 0.2s;
        }

        .price-tag:hover {
          background: #1f2937;
          color: white;
          border-color: #1f2937;
          box-shadow: 0 4px 16px rgba(0,0,0,0.25);
        }

        .price-tag.selected {
          background: #2563eb;
          color: white;
          border-color: #2563eb;
          transform: scale(1.1);
        }
      `}</style>

      {/* Property Info Count */}
      <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg px-4 py-2 z-10">
        <p className="text-sm font-semibold text-gray-700">
          {filteredProperties.length} {filteredProperties.length === 1 ? 'property' : 'properties'}
        </p>
      </div>

      {/* Property Preview Modal - Full Screen Overlay */}
      {selectedProperty && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={closePreview}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closePreview}
              className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 z-10 transition"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="cursor-pointer hover:opacity-95 transition"
              onClick={() => handlePropertyCardClick(selectedProperty)}
            >
              {/* Property Image */}
              {selectedProperty.images && selectedProperty.images[0] && (
                <div className="relative h-64 sm:h-96">
                  <img
                    src={thumbUrl(selectedProperty.images[0])}
                    alt={selectedProperty.title}
                    className="w-full h-full object-cover rounded-t-2xl"
                  />
                </div>
              )}

              {/* Property Details */}
              <div className="p-6 sm:p-8">
                <div className="flex items-start justify-between mb-3">
                  <h2 className="font-bold text-2xl sm:text-3xl text-gray-900 flex-1">
                    {selectedProperty.title}
                  </h2>
                  <div className="flex items-center text-base ml-4">
                    <span className="text-yellow-500 mr-1">⭐</span>
                    <span className="font-semibold">{selectedProperty.rating.toFixed(1)}</span>
                    <span className="text-gray-500 ml-1">({selectedProperty.reviews_count})</span>
                  </div>
                </div>
                
                <p className="text-base text-gray-600 mb-4 flex items-center">
                  <MapPin className="w-4 h-4 mr-1" />
                  {selectedProperty.location}
                </p>

                <div className="text-base text-gray-600 mb-6 flex items-center gap-4">
                  <span>{selectedProperty.guests} guests</span>
                  <span>·</span>
                  <span>{selectedProperty.bedrooms} bedroom</span>
                  <span>·</span>
                  <span>{selectedProperty.beds} bed</span>
                  <span>·</span>
                  <span>{selectedProperty.baths} bath</span>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <div>
                    <span className="text-3xl font-bold text-gray-900">
                      ${selectedProperty.price}
                    </span>
                    <span className="text-base text-gray-600 ml-2">/ night</span>
                  </div>
                  <button className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition font-semibold text-lg">
                    View Full Details →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyMapView;
